<script setup lang="ts">
/**
 * LoadingScreen — host 的 loading 遮罩。**版面對齊競品 DOM**（2026-08-20）。
 *
 * 競品結構（`#loading-container` → 進度條 + `#tips-text`）：
 *
 * ```html
 * <div class="progress-bar-container-port">
 *   <div class="progress-bar-background"></div>
 *   <div class="progress-bar-fill-container">          ← 裁切層
 *     <div class="progress-bar-fill stripes" style="background-color: rgb(180,49,245); width: 50%">
 *       <div class="top-highlight"></div>              ← 頂緣亮線
 *       <div class="front-highlight"></div>            ← 進度前緣亮塊
 *     </div>
 *   </div>
 *   <div class="progress-bar-outline border-inner"></div>   ← 兩層外框疊在填充**之上**
 *   <div class="progress-bar-outline border-outer"></div>
 * </div>
 * <p class="text-port"><span>正在登录</span><span class="tips-text-child2 tips-text-child2-hidden">[50%]</span></p>
 * ```
 *
 * 從 DOM 讀出來的三個決定（其中兩個跟第一版暫時稿不同）：
 *   1. **填充色是 inline `background-color`** → per-game 主題色。我們用 `var(--color-theme)`。
 *   2. 🛑 **文字是「階段文字」不是「加载中 N%」**：競品第一個 span 是真實階段
 *      （實測 6 句：正在加载 → 正在初始化 → 正在加载游戏 → 正在登录 → 正在读取信息 → 正在加载资源），
 *      百分比在第二個 span。host 本來就知道自己在哪個階段 → **文字誠實、只有進度是假的**。
 *   3. `-port` 後綴 = 直向版（競品另有橫向版）。host 目前只做直向。
 *
 * **輪播小提示**（`#animated_text`）也做了 —— 🛑 它跟階段文字（`#tips-text`）是**兩個不同的東西**、
 * 競品兩者同時存在：階段文字說「現在在做什麼」，輪播的是跟載入無關的小知識。
 * ⏳ 文案暫時捏造（只有 `cache` 那句是競品實測逐字），待確認。
 * **競品底部的 `#footer-container`（主題色漸層 + 版權圖）刻意不做**（使用者 2026-08-20 決定），
 * splash 背景也維持我們自己的 per-game `bgUrl`，不抄競品的 blob 大圖。
 *
 * ✅ **進度條本體的 CSS 已拿到並逐項照抄**（2026-08-20 第二批）：`3.5px` 圓角、`#30a2d0` 預設填充色、
 *    `background-size: 8.7px 100%` 的斜紋、`-75deg` 漸層、`animate-stripes` 1s 線性移 `34.7px`、
 *    `top-highlight` 是**平塗 20% 白 + 高 50%**（不是漸層）、`front-highlight` 是
 *    `90deg 透明→#fff`、`width:50%` 但 `max-width:20px`。px → rem 除以 10（專案 375 設計稿慣例）。
 *    兩層外框也已照抄（🛑 `inner` 比 `outer` 粗，見樣式區註記）。
 * ⏳ **還缺**：`.progress-bar-container-port` / `.progress-bar-background` / `#loading-container` /
 *    `.text-port` → **條高**、底色、整體位置仍是暫定（條高 0.7rem 是**從 3.5px 圓角反推**的
 *    pill 形，待確認）。
 *
 * 🛑 **兩種完全不同的畫面，由 `hostLoading` prop 切**（= Cocos `GAME_READY.payload.hostLoading`）：
 *   - `false`（舊款 / 還沒收到 GAME_READY / **開頭 splash 未滿 `LOGO_SPLASH_MS`**）
 *     → **只有原本的 logo 動畫**。進度條、階段文字、
 *     輪播小提示、per-game 背景圖**一個都不渲染**，舊款畫面 100% 不變。
 *   - `true` → **per-game 背景圖 + 進度條 + 階段文字 + 輪播小提示**，且**不顯示 logo**。
 * `showStart` → 進度條換「開始遊戲」鈕，按下 emit `start` → `closeLoading()` → 送 LOADING_CLOSED。
 *
 * 🛑 **版面盒子跟 paytable / rules 彈窗共用**（使用者 2026-08-20）：`.loading-frame` 的寬度是
 *    `var(--card-w, min(100vw, calc(100dvh * 9/16)))` —— 與 `PopupWrapper` 的 `.popup-frame`
 *    **同一條公式、同一個 CSS var**，所以 loading 畫面、彈窗、Cocos 卡片三者永遠等寬。
 *    per-game 背景圖是鋪在**這個框內**，不是整個 viewport 滿版。
 *    改 `.responsive-card` / `.popup-frame` 公式時，這裡也要跟著動。
 *
 * 協議見 `docs/LOADING_PROTOCOL.md`。
 */
import { resolveImg } from '@/composables/useGameImages'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import LogoAnimation from '../assets/logo_animation.webp'

/**
 * 輪播小提示的句子（`common.loading.tips.*`，**逐字對齊競品**）。加減句子改這裡 + locale。
 */
const TIP_KEYS = ['device', 'idlePages', 'wifi', 'browser', 'cache', 'firstLoad'] as const
/** 每句停留多久。2026-08-21 定案 4s（競品間隔未實測，由使用者拍板） */
const TIP_INTERVAL_MS = 4_000

/** 階段（對應競品 `#tips-text` 第一個 span）。文案在 `common.loading.phase.*` */
export type LoadingPhase = 'login' | 'game' | 'assets' | 'complete'
/**
 * 🛑 **只有「正在加载资源」那一段帶百分比** —— 競品其餘階段那個 span 都掛 `-hidden`
 * （使用者提供的文案表也只有 `Loading resources [?%]` 標了百分比）。
 */
const PHASE_WITH_PERCENT: LoadingPhase = 'assets'

const props = withDefaults(
  defineProps<{
    loading: boolean
    /** 假進度 0~100（useHostLoading.progress） */
    progress?: number
    /** 目前階段 → 進度條下方文字 */
    phase?: LoadingPhase
    /** 進度條換成「開始遊戲」按鈕（新款 LOAD_COMPLETE + 補滿後） */
    showStart?: boolean
    /**
     * **這款走新 loading 流程**（= Cocos 在 `GAME_READY.payload` 帶了 `hostLoading: true`）。
     *
     * 🛑 這是**唯一**的顯示開關（使用者 2026-08-20 定案）：
     *   - `false`（舊款、或還沒收到 `GAME_READY`）→ **只有原本的 logo 動畫**，
     *     進度條 / 階段文字 / 輪播小提示**一個都不出**，舊款畫面完全不變。
     *   - `true` → 換成 **per-game loading 背景圖 + 進度條 + 階段文字 + 輪播小提示**，
     *     且**不再顯示 logo** —— 那整組是疊在背景圖上的，不是疊在 logo 畫面上。
     *
     * ⚠️ 時序上的代價（見 docs/LOADING_PROTOCOL.md〈十二〉）：旗標要等 `GAME_READY` 才到，
     *    在那之前只能顯示 logo → 畫面會有一次「logo → 背景圖 + 進度條」的切換。
     */
    hostLoading?: boolean
    /** per-game 背景圖（bundled URL；undefined → 黑底） */
    bgUrl?: string
    /**
     * per-game loading 配色的 CSS vars（`src/config/loadingTheme.ts` 的 `resolveLoadingTheme`）。
     * 掛在 `.loading-frame` 上 → 進度條填充與開始鈕底色吃得到。
     * 空物件 = 該款沒設 → CSS 端 fallback 到 `var(--color-theme)`。
     */
    themeVars?: Record<string, string>
  }>(),
  {
    progress: 0,
    phase: 'login',
    showStart: false,
    hostLoading: false,
    bgUrl: undefined,
    themeVars: () => ({}),
  },
)

const emit = defineEmits<{
  /** 玩家按「開始遊戲」 */
  start: []
}>()

const { t } = useI18n()

// 是否显示加载界面
const showLoading = ref(true)

// 监听 loading 状态
watch(
  () => props.loading,
  (loading) => {
    if (!loading) {
      // 加载完成，延迟 500ms 后隐藏
      setTimeout(() => {
        showLoading.value = false
      }, 500)
    } else {
      showLoading.value = true
    }
  },
)

const pct = computed(() => Math.max(0, Math.min(100, Math.round(props.progress))))

/**
 * 🛑 **淡出期間凍結「顯示按鈕還是進度條」** —— 不凍會閃一下。
 *
 * 玩家按下「開始」→ `closeLoading()` → `maskActive` **當場**變 false →
 * 上游的 `showStartButton` 跟著變 false → 版面切回「進度條 + 階段文字 + 輪播提示」那半邊，
 * 但遮罩還要 500ms 才淡完 → 玩家會看到按鈕消失、進度條閃回來一下才整個淡掉。
 *
 * 作法：`props.loading` 為 true（還在載入中）時才同步 `showStart`；一旦轉 false（開始淡出）
 * 就保持最後一幀 → 按下去看到的是「按鈕留在原地一起淡掉」。
 * kill switch（`autoClose`，沒有按鈕那條路）同理：關閉當下 `showStart` 還是 false，
 * 凍住之後假進度補滿也不會讓按鈕在淡出中途冒出來。
 */
const renderShowStart = ref(props.showStart)
watch(
  () => [props.loading, props.showStart] as const,
  ([loading, next]) => {
    if (loading) renderShowStart.value = next
  },
  { immediate: true },
)
/** 只有「正在加载资源」那段顯示 `[N%]`（見 PHASE_WITH_PERCENT） */
const showPercent = computed(() => props.phase === PHASE_WITH_PERCENT)

// ── 輪播小提示（競品 #animated_text）────────────────────────────
// 🛑 跟階段文字（#tips-text）是**兩個不同的東西**，競品兩者同時存在：
//    階段文字說「現在在做什麼」，這裡是跟載入無關的小知識。
const tipIdx = ref(0)
let tipTimer: ReturnType<typeof setInterval> | undefined
const stopTips = (): void => {
  if (tipTimer !== undefined) {
    clearInterval(tipTimer)
    tipTimer = undefined
  }
}
watch(
  showLoading,
  (on) => {
    stopTips()
    if (!on) return
    tipIdx.value = 0
    tipTimer = setInterval(() => {
      tipIdx.value = (tipIdx.value + 1) % TIP_KEYS.length
    }, TIP_INTERVAL_MS)
  },
  { immediate: true },
)
onBeforeUnmount(stopTips)

// ── 背景圖淡入（永不黑屏保底的第二半）────────────────────────────
/**
 * 背景圖**已經解碼可以畫了**。`false` 期間 `.loading-bg` 是 `opacity: 0`，
 * 露出 `.loading-frame` 的保底底色（per-game 主題色調暗，見 `config/loadingTheme.ts`）。
 *
 * 🛑 為什麼不是「等它自己畫上去就好」：`<img>` 在圖到達前是**完全透明**的，
 *    而 `.loading-frame` 本來沒有底色 → 那段空窗露出的是 host 大底（實測死灰 `#333`）。
 *    底色 + 淡入是一組：底色負責「不是黑的」，淡入負責「圖到的那一下不是硬切」。
 */
const bgReady = ref(false)
/**
 * 快取命中（preload 已抓好）時 `load` 事件仍會非同步觸發，理論上 `@load` 就夠；
 * 這支只是保險 —— 元素掛上當下若已 `complete` 就直接放行，不賭事件。
 */
const onBgRef = (el: unknown): void => {
  const img = el as HTMLImageElement | null
  if (img && img.complete && img.naturalWidth > 0) bgReady.value = true
}
// 換款 / 換語系 → 換圖，重新等新的那張（否則舊的 ready 狀態會讓新圖沒有淡入直接跳出來）
watch(
  () => props.bgUrl,
  () => {
    bgReady.value = false
  },
)
</script>

<template>
  <Transition name="loading-fade">
    <div v-if="showLoading" class="loading-mask" :class="{ 'loading-mask--legacy': !hostLoading }">
      <!-- 內框：**跟 paytable / rules 彈窗同一個盒子** —— 寬度共用 `--card-w`
           （`PopupWrapper` 的 `.popup-frame` 同源，對齊 Cocos 卡片）、高度 100%、水平置中。
           🛑 舊款（legacy）維持原本的**全屏黑底 + logo**，不套這個框（避免動到舊款畫面）。 -->
      <div class="loading-frame" :style="themeVars">
        <!-- 舊款（或 GAME_READY 還沒到）：只有原本的 logo 動畫，下面整組都不出 -->
        <!-- 🛑 `fetchpriority`（2026-08-28）：這張是 **894KB 的動態 webp**
             （294×294、178 幀、3.04s、~58.6fps、無限迴圈）。它跟 per-game 背景圖幾乎同時
             開始下載，不降級的話會把頻寬吃光 —— 實測 stg rtt200 它 619→1254ms 佔滿，
             背景圖被擠到後面。降成 `low` 讓標了 `fetchpriority="high"` 的背景圖 preload 先走。
             🛑 **只有這款有背景圖時才降級**（`bgUrl` 為空 → 綁 `undefined` → Vue 直接不輸出這個
                attribute）：舊款畫面上只有這張 logo，沒有第二張圖要跟它搶，降級只會讓 logo 晚到，
                所以舊款維持與改動前**逐字元相同**的標記。
             ⚠️ 只動優先權，**檔案本身沒有動**（瘦身要設計師重出圖）。 -->
        <img
          v-if="!hostLoading"
          :src="resolveImg(LogoAnimation)"
          alt="logo"
          :fetchpriority="bgUrl ? 'low' : undefined"
          class="loading-logo object-contain"
        />

        <template v-else>
          <!-- per-game 背景（bundled URL，不走 resolveImg）。進度條那組是疊在**它**上面，不是疊在 logo 上。
               載完（`@load` / 掛上時就已 `complete`）才淡入蓋住 `.loading-frame` 的保底底色。 -->
          <img
            v-if="bgUrl"
            :ref="onBgRef"
            :src="bgUrl"
            alt=""
            draggable="false"
            class="loading-bg"
            :class="{ 'loading-bg--ready': bgReady }"
            @load="bgReady = true"
          />

          <!-- 🛑 **不置中**：背景圖是整張直式主視覺，標題在中間 —— 進度條置中會壓在字上。
             照競品版位放在畫面 74.5% 高度（競品 640 設計稿的 top: 477）。 -->
          <div class="loading-stack">
            <!-- 進度條 + 階段文字 ⇄ 開始鈕（同一槽位，切換時不跳版） -->
            <div class="loading-slot">
              <button
                v-if="renderShowStart"
                type="button"
                class="loading-start"
                data-testid="loading-start"
                @click="emit('start')"
              >
                <!-- 競品 `.start-button-inner`：整片白色漸層 + 細白邊（不是只有頂部內光） -->
                <span class="loading-start-inner" />
                <span class="loading-start-text">{{ t('common.loading.start') }}</span>
              </button>

              <template v-else>
                <!-- 進度條：背景 → 裁切層(填充 + 兩道亮部) → 內框 → 外框（層序照競品） -->
                <div
                  class="progress-bar-container"
                  role="progressbar"
                  :aria-valuenow="pct"
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  <div class="progress-bar-background" />
                  <div class="progress-bar-fill-container">
                    <div class="progress-bar-fill stripes" :style="{ width: `${pct}%` }">
                      <div class="top-highlight" />
                      <div class="front-highlight" />
                    </div>
                  </div>
                  <div class="progress-bar-outline border-inner" />
                  <div class="progress-bar-outline border-outer" />
                </div>

                <!-- 階段文字（+ 預設隱藏的百分比，對齊競品 #tips-text 兩個 span） -->
                <p class="tips-text">
                  <span>{{ t(`common.loading.phase.${phase}`) }}</span>
                  <span v-if="showPercent" class="tips-text-percent">
                    {{ t('common.loading.percent', { n: pct }) }}
                  </span>
                </p>
              </template>
            </div>

            <!-- 輪播小提示（競品 #animated_text；載完換成開始鈕後就不再需要 → 收起） -->
            <div v-if="!renderShowStart" class="loading-tips">
              <Transition name="tip">
                <p :key="tipIdx" class="loading-tip">
                  {{ t(`common.loading.tips.${TIP_KEYS[tipIdx]}`) }}
                </p>
              </Transition>
            </div>
          </div>
          <!-- 版本角標槽（App.vue 塞 VersionBadge）：只在**新版 per-game loading** 出
               （🛑 刻意放在 v-else 內，legacy 全屏 logo 不顯示）；absolute 貼
               .loading-frame 左下 = loading 背景圖左下角，跟遮罩一起 fade。 -->
          <slot />
        </template>
      </div>
    </div>
  </Transition>
</template>

<style>
/* 遮罩底：新款透明（框外露出 host 背景，跟彈窗的 overlay 一致）；
 * 舊款維持原本的全屏黑底（`--legacy`）。 */
.loading-mask {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-mask--legacy {
  background: #000;
}
/* 內框：寬度跟 `.popup-frame` 同源（`--card-w`，對齊 Cocos 卡片）；var() 失效退回同一條 CSS 公式。
 * 🛑 改 `.responsive-card` / `.popup-frame` 的公式時，這裡也要跟著動。 */
.loading-frame {
  position: relative;
  width: var(--card-w, min(100vw, calc(100dvh * 9 / 16)));
  height: 100%;
  margin: 0 auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
/* 舊款：框不限寬（維持全屏 logo 置中的原樣） */
.loading-mask--legacy .loading-frame {
  width: 100%;
}
/* 🛑 **永不黑屏保底**（2026-08-28）：新版版面切換的那一刻背景圖還沒下載完，
 * 這個框本來是透明的 → 露出 host 大底（實測是死灰 `#333`），看起來像畫面掛了。
 * `--loading-bg-fallback` = 該款主題色調暗（`config/loadingTheme.ts` 的 `mixTowardBlack`）。
 * 🛑 預設值刻意是 `transparent` 而**不是** `#000`：沒設 per-game 主題色的款維持改動前的行為，
 *    這條規則對它們就是完全的 no-op（也就沒有「本來透明變成黑」的退步風險）。 */
.loading-mask:not(.loading-mask--legacy) .loading-frame {
  background-color: var(--loading-bg-fallback, transparent);
}

.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: opacity 0.5s ease-out;
}

.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
}

/* 中间 logo 动画图 — 純 CSS,跟 viewport 寬走,不依賴 container。
 * `width: 100%` 不要設(讓 image 用自然寬,被 max-width 卡住),否則 image 會被
 * 拉到滿 max-width,反而看起來太大。
 * height: auto 維持原圖比例。 */
.loading-logo {
  position: relative; /* 壓在 .loading-bg 之上 */
  max-width: min(50vw, 400px);
  height: auto;
}
/* 背景圖：載完前 `opacity: 0`（露出 `.loading-frame` 的保底底色），載完加
 * `--ready` 淡入蓋上去。硬切會看到底色「啪」一下換成整張圖，淡入柔和很多。 */
.loading-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  max-width: none;
  opacity: 0;
  transition: opacity 0.28s ease-out;
}
.loading-bg--ready {
  opacity: 1;
}
/* 使用者要求減少動態 → 不做淡入，直接顯示（底色仍在，一樣不會有黑屏） */
@media (prefers-reduced-motion: reduce) {
  .loading-bg {
    transition: none;
  }
}

/* ── 進度條 / 開始鈕 槽位 ─────────────────────────────────────────
 * ✅ 標「競品 …」的是**照抄實值**；⏳ 標的還是暫定（那幾支 CSS 還沒拿到），
 *    到手後只改這一區的數字，結構不用動。 */
/* 進度條 + 文字 + 提示整組的**垂直版位**：競品 #loading-container 在 640 設計稿的 top: 477
 * → 74.5%。背景圖是整張直式主視覺（標題在中間），置中會壓到字，所以照競品放低。 */
.loading-stack {
  --loading-stack-top: 74.5%;

  position: absolute;
  left: 0;
  right: 0;
  top: var(--loading-stack-top);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading-slot {
  /* 版面（競品 #loading-container：flex column、align-items:center、寬 360px）
   * px → rem 除以 10（專案 375 設計稿慣例：html font-size = 卡片寬 / 37.5）。
   * 擷取當下競品 viewport 寬 360px、dpr 2。 */
  --loading-slot-gap: 0.4rem; /* 競品 #tips-text margin-top: 4px */
  /* 進度條（競品 #progress-bar-container-port：212 × 13） */
  --loading-bar-w: 21.2rem;
  --loading-bar-h: 1.3rem;
  --loading-bar-radius: 0.35rem; /* 競品 3.5px */
  --loading-bar-bg: #111; /* 競品 .progress-bar-background */
  /* 填充區相對底框內縮（競品 .progress-bar-fill-container 四邊 0.87px）→ 底色露出一圈細邊 */
  --loading-bar-inset: 0.087rem;
  /* 兩層外框（競品實值）。🛑 **名字是反直覺的：`inner` 比 `outer` 粗**
   * （inner 1.7px #272727 / outer 0.85px #111），而且兩層都是 `inset:0` 疊在同一個框上、
   * DOM 順序 inner 在前 → outer 畫在上面。淨效果 = 最外 0.85px 近黑 + 內側 0.85px 深灰
   * 的雙色細框（總厚 1.7px）。別「順手」把兩者對調或合併成單一 border。 */
  --loading-border-inner: 0.17rem solid #272727; /* 競品 1.7px */
  --loading-border-outer: 0.085rem solid #111; /* 競品 .85px */
  /* 斜紋（競品 background-size: 8.7px 100%；keyframe 移 34.7px / 1s ≈ 4 個週期） */
  --loading-stripe-size: 0.87rem;
  --loading-stripe-shift: 3.47rem;
  --loading-stripe-speed: 1s;
  /* 文字（競品 #tips-text：10.3px / #fff / center） */
  --loading-text-size: 1.03rem;
  --loading-text-color: #ffffff;

  position: relative; /* 壓在 .loading-bg 之上 */
  width: var(--loading-bar-w);
  min-height: 4.4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--loading-slot-gap);
}

.progress-bar-container {
  position: relative;
  width: var(--loading-bar-w);
  height: var(--loading-bar-h);
}
/* 1. 底 */
.progress-bar-background {
  position: absolute;
  inset: 0;
  border-radius: var(--loading-bar-radius);
  background: var(--loading-bar-bg);
}
/* 2. 裁切層：填充超出圓角的部分切掉。
 * 🛑 競品是**四邊各內縮 0.87px**（不是 inset:0）→ 底色 #111 會在填充外圍露出一圈細邊，
 *    那圈細邊 + 兩層外框才是完整的框感。改成 inset:0 會少一層。 */
.progress-bar-fill-container {
  position: absolute;
  top: var(--loading-bar-inset);
  right: var(--loading-bar-inset);
  bottom: var(--loading-bar-inset);
  left: var(--loading-bar-inset);
  border-radius: var(--loading-bar-radius);
  overflow: hidden;
}
/* 3. 填充（`width` 由 inline style 給，同競品；競品該規則的 `width: 0` 就是被 inline 蓋掉的）
 * 🛑 斜紋是**填充自己的 background-image**（同一個元素上 `.progress-bar-fill.stripes`），
 *    不是 ::after —— 所以 `background-size` 在 fill 上、`background-image` 在 stripes 上，
 *    動的是同一份 background-position。別改回偽元素，會多一層合成。 */
.progress-bar-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  border-radius: var(--loading-bar-radius);
  /* 🛑 **不要**在這裡（或 `.loading-slot`）宣告 `--loading-bar-fill` —— per-game 配色掛在外層
   *    `.loading-frame`（`resolveLoadingTheme`），自己宣告會把它 shadow 掉。這裡只「讀」：
   *    per-game 色 → 該款主題色 → 競品 CSS 預設 #30a2d0。 */
  background-color: var(--loading-bar-fill, var(--color-theme, #30a2d0));
  background-size: var(--loading-stripe-size) 100%;
  backface-visibility: hidden;
  /* 競品沒有這條（他們逐幀設 width）；我們的假進度每 100ms 跳一次，不補就是階梯狀 */
  transition: width 0.12s linear;
}
/* 斜紋（競品 `.stripes`：-75deg、白 10%、35%~75% 帶狀） */
.stripes {
  background-image: linear-gradient(
    -75deg,
    hsla(0, 0%, 100%, 0) 35%,
    hsla(0, 0%, 100%, 0.1) 0,
    hsla(0, 0%, 100%, 0.1) 75%,
    hsla(0, 0%, 100%, 0) 0,
    hsla(0, 0%, 100%, 0)
  );
  animation: animate-stripes var(--loading-stripe-speed) linear infinite;
}
@keyframes animate-stripes {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: var(--loading-stripe-shift) 0;
  }
}
/* 頂緣亮部：競品是**平塗 20% 白、高 50%**（不是漸層），只圓上面兩角 */
.top-highlight {
  position: absolute;
  transform: translateZ(0);
  width: 100%;
  height: 50%;
  background-color: hsla(0, 0%, 100%, 0.2);
  border-radius: var(--loading-bar-radius) var(--loading-bar-radius) 0 0;
}
/* 進度前緣亮塊：透明 → 純白，寬 50% 但**最多 20px**（條短時等比縮、條長時封頂） */
.front-highlight {
  position: absolute;
  transform: translateZ(0);
  right: 0;
  height: 100%;
  width: 50%;
  max-width: 2rem;
  background-image: linear-gradient(90deg, hsla(0, 0%, 100%, 0), #fff);
  border-radius: 0 var(--loading-bar-radius) var(--loading-bar-radius) 0;
}
/* 4/5. 兩層外框（疊在填充之上，同競品層序）
 * ✅ 共用規則照抄競品 `.progress-bar-outline`：absolute + 四邊 0 + 圓角 3.5px + translateZ(0)。
 *    競品這條**只有定位、沒有 border / background** —— 線本身在 `.border-inner` / `.border-outer`
 *    兩個變體裡（✅ 已拿到，見上方 CSS var 區的說明）。 */
.progress-bar-outline {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: var(--loading-bar-radius);
  transform: translateZ(0);
  pointer-events: none;
}
.progress-bar-outline.border-outer {
  border: var(--loading-border-outer);
}
.progress-bar-outline.border-inner {
  border: var(--loading-border-inner);
}

/* 階段文字（競品 #tips-text：`<p>`、margin: 4px 0 0、10.3px、#fff、text-align:center） */
.tips-text {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: var(--loading-text-size);
  color: var(--loading-text-color);
  text-align: center;
}
.tips-text-percent {
  font-variant-numeric: tabular-nums;
}

/* 輪播小提示（競品 #animationTipsContainer 360×35 / #animated_text 10px、line-height 13px、#fff、center）
 * 固定高度 + 絕對定位的子項 → 換句時版面不跳。 */
.loading-tips {
  position: relative;
  margin-top: 1.4rem;
  width: min(90vw, 36rem);
  height: 3.5rem;
  overflow: hidden;
}
.loading-tip {
  position: absolute;
  inset: 0;
  margin: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  text-align: center;
  font-size: 1rem;
  line-height: 1.3rem;
  color: #ffffff;
}
/* 由下往上滑入 / 往上滑出（競品 class 名 `animated-text-reset-to-bottom-port` 的語意） */
.tip-enter-active,
.tip-leave-active {
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
}
.tip-enter-from {
  opacity: 0;
  transform: translateY(1.6rem);
}
.tip-leave-to {
  opacity: 0;
  transform: translateY(-1.6rem);
}

/* ── 開始鈕（競品 `#__startedButton`，實測值逐項照抄）────────────────────────
 * `.start-button-container-port`：**151.7 × 32**、`position:absolute` + `left/right:0; margin:auto`
 *   置中、`top: 481`（＝進度條 `top:477` 幾乎同位 → **原地取代進度條**，所以我們放同一個槽位）。
 * `.start-button`：`border: 2px solid rgba(0,0,0,.15)`、`border-radius: 8px`；
 *   `background-color` 與 `text-shadow` 都由 **inline 帶 per-game 色**（實測 `rgb(155,5,255)`）。
 * `.start-button-inner`：`inset:0`、`linear-gradient(180deg, 白.5 → 白0)`、
 *   `border: .87px solid 白.4`、`border-radius: 6px`、`background-origin: border-box`。
 * `button .text-port`：**12px / weight 900**。
 * `.start-button-show-port`：`animation-name: show-bounce`（✅ 定案用自製彈入，見下 keyframes）。
 *
 * ⚠️ 競品按鈕的紫 `#9B05FF` 比進度條的紫 `#B431F5` **更深** —— 同一款主題色的深色變體。
 *    → per-game 配色設在 `src/config/loadingTheme.ts`（`bar` / `button` 兩個值）。 */
.loading-start {
  position: relative;
  width: 15.17rem; /* 競品 151.7px */
  height: 3.2rem; /* 競品 32px */
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0.2rem solid rgba(0, 0, 0, 0.15); /* 競品 2px */
  border-radius: 0.8rem; /* 競品 8px */
  /* 🛑 同上：只讀不宣告（per-game 配色掛在 .loading-frame）。 */
  background-color: var(--loading-start-bg, var(--color-theme, #30a2d0));
  /* 🛑 **文字固定白色**（使用者 2026-08-20 定案）—— 不要接主題色 / 不要開成旋鈕。
   *    底色才是 per-game（跟進度條填充同一個值，見 src/config/loadingTheme.ts）。 */
  color: #ffffff;
  font-size: 1.2rem; /* 競品 button .text-port 12px */
  font-weight: 900;
  text-shadow: 0 0.1rem 0.2rem var(--loading-start-bg, var(--color-theme, #30a2d0)); /* 競品 inline：同色 0 1px 2px */
  animation: show-bounce 0.35s ease-out; /* 競品 .start-button-show-port */
}
.loading-start-inner {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border: 0.087rem solid hsla(0, 0%, 100%, 0.4); /* 競品 .87px */
  border-radius: 0.6rem; /* 競品 6px（比外框 8px 小一圈） */
  background-image: linear-gradient(180deg, hsla(0, 0%, 100%, 0.5), hsla(0, 0%, 100%, 0));
  background-origin: border-box;
  pointer-events: none;
}
.loading-start-text {
  position: relative; /* 壓在 inner 之上 */
}
.loading-start:active {
  filter: brightness(0.9);
}
/* 競品 `show-bounce` 的 keyframes 沒拿到 → 自製彈入。2026-08-21 **定案就用這版**，不再追競品原值。 */
@keyframes show-bounce {
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  60% {
    opacity: 1;
    transform: scale(1.08);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
